// LIVE-09 警報彈窗：使用者選哪些事件要自動帶出畫面（感測器 / 警報 / 偵測）、帶到哪裡（浮窗，或影像牆的指定格位）。
// 偏好存在這台瀏覽器（localStorage）：哪個螢幕要彈、彈到哪一格是工作站的事，不跟帳號走。
import { useEffect, useState } from 'react';

export interface PopupPrefs {
  /** 感測器（門磁 / PIR）觸發、且有「即時彈窗」關聯的攝影機 */
  sensor: boolean;
  /** 警報（煙霧 / 拆除，或布防下設了警報的偵測）→ 來源攝影機 */
  alarm: boolean;
  /** 一般 AI 偵測（人 / 車…）→ 來源攝影機 */
  detection: boolean;
  /** float = 右下角浮窗；cell = 人在影像牆時換到指定格位（不在影像牆就用浮窗） */
  target: 'float' | 'cell';
  /** 指定格位（0 起算） */
  cell: number;
}

export const DEFAULT_POPUP_PREFS: PopupPrefs = { sensor: true, alarm: true, detection: false, target: 'float', cell: 0 };
const KEY = 'kinio.vms.alarmPopup.v1';
const CHANGED = 'kinio-vms-alarm-popup';

export function parsePopupPrefs(raw: string | null): PopupPrefs {
  try {
    const v = JSON.parse(raw ?? '') as Partial<PopupPrefs>;
    const cell = Number.isInteger(v.cell) && (v.cell as number) >= 0 && (v.cell as number) < 64 ? (v.cell as number) : 0;
    return {
      sensor: typeof v.sensor === 'boolean' ? v.sensor : DEFAULT_POPUP_PREFS.sensor,
      alarm: typeof v.alarm === 'boolean' ? v.alarm : DEFAULT_POPUP_PREFS.alarm,
      detection: typeof v.detection === 'boolean' ? v.detection : DEFAULT_POPUP_PREFS.detection,
      target: v.target === 'cell' ? 'cell' : 'float',
      cell,
    };
  } catch {
    return { ...DEFAULT_POPUP_PREFS };
  }
}

export function loadPopupPrefs(): PopupPrefs {
  try {
    return parsePopupPrefs(localStorage.getItem(KEY));
  } catch {
    return { ...DEFAULT_POPUP_PREFS };
  }
}

export function savePopupPrefs(p: PopupPrefs) {
  try {
    localStorage.setItem(KEY, JSON.stringify(p));
  } catch {
    /* 私密模式：只在這一頁生效 */
  }
  window.dispatchEvent(new CustomEvent(CHANGED, { detail: p }));
}

/** 偏好（同一頁的其他元件改了會跟著變）。 */
export function usePopupPrefs(): [PopupPrefs, (next: PopupPrefs) => void] {
  const [prefs, setPrefs] = useState<PopupPrefs>(() => loadPopupPrefs());
  useEffect(() => {
    const on = (e: Event) => setPrefs((e as CustomEvent<PopupPrefs>).detail);
    window.addEventListener(CHANGED, on);
    return () => window.removeEventListener(CHANGED, on);
  }, []);
  return [prefs, savePopupPrefs];
}

/** 這個分級的攝影機事件要不要彈（感測器另外走關聯）。 */
export function cameraPopupWanted(level: string, prefs: PopupPrefs): boolean {
  if (level === 'alarm') return prefs.alarm;
  if (level === 'detection') return prefs.detection;
  return false;
}

// ---- 影像牆的指定格位 ----

type WallHandler = (camKey: string, cell: number) => boolean;
let wall: WallHandler | null = null;

/** 影像牆在畫面上時登記；回傳取消登記。 */
export function registerWallPopup(h: WallHandler): () => void {
  wall = h;
  return () => {
    if (wall === h) wall = null;
  };
}

/** 帶到影像牆的指定格位；回 false = 影像牆不在畫面上（或格位超出目前版面），呼叫端改用浮窗。 */
export function popupToWall(camKey: string, cell: number): boolean {
  return wall ? wall(camKey, cell) : false;
}

// ---- 浮動通知只有一套（VMS UX 評審第三輪 R3-P0-2、B3-02；SPEC UX-14 ⑥） ----
// 警報浮窗（這裡）與收件匣的浮動通知（features/notifications/Inbox）共用右下角：
//   - 浮窗在畫面上時，收件匣不跳浮動通知（排隊，等浮窗收掉再補）
//   - 浮窗帶出過的設備，2 分鐘內收件匣同一台的警報類通知不再跳（只進收件匣）

const DUP_MS = 120_000;
const popped = new Map<string, number>();
let active = 0;
const subs = new Set<() => void>();

/** 浮窗帶出了這台設備的警報（key = <home>/<device_ref>）。 */
export function notePopped(deviceKey: string, now = Date.now()): void {
  popped.set(deviceKey, now);
  for (const [k, at] of popped) if (now - at > DUP_MS) popped.delete(k);
}

/** 收件匣的這一則（同一台）剛被浮窗帶出過嗎。 */
export function poppedRecently(deviceKey: string, now = Date.now()): boolean {
  const at = popped.get(deviceKey);
  return at !== undefined && now - at <= DUP_MS;
}

/** 浮窗目前展開著幾張（0 = 沒有，收件匣可以跳）。 */
export function setPopupActive(n: number): void {
  if (active === n) return;
  active = n;
  for (const f of subs) f();
}

export function popupActive(): number {
  return active;
}

export function subscribePopupActive(f: () => void): () => void {
  subs.add(f);
  return () => subs.delete(f);
}

// ---- 收件匣的警報類一律走警報卡（VMS UX 評審第四輪 R4-P0-1、B4-01 / B4-02；SPEC UX-14 ⑥） ----
// 收件匣（notify）來的警報類通知，有設備（home_id + device_ref）的不另跳舊樣式浮動通知，交給警報卡佇列：
// 同一種樣式（快照、處理鈕、20 秒收合），同一台剛被事件串流帶出過就不重複。沒有設備的（帳號安全、去識別的警報）照樣用浮動通知。

export interface InboxAlarm {
  nid: string;
  homeId: string;
  deviceRef: string;
  eventId?: string;
  /** 收件匣找到的完整設備名（找不到 = ''，卡片用 device_ref 末碼代替） */
  name: string;
  /** 事件顯示名（notify 的內文；沒有用標題） */
  what: string;
  /** created_at（ms） */
  at: number;
}

const inboxSubs = new Set<(a: InboxAlarm) => void>();

/** 警報卡在畫面上（訂閱著）才收；回 false = 這個視窗沒有警報卡（例：多螢幕的非警報視窗），呼叫端照舊跳浮動通知。 */
export function offerInboxAlarm(a: InboxAlarm): boolean {
  if (!inboxSubs.size) return false;
  for (const f of inboxSubs) f(a);
  return true;
}

export function subscribeInboxAlarms(f: (a: InboxAlarm) => void): () => void {
  inboxSubs.add(f);
  return () => inboxSubs.delete(f);
}

// ---- 已經跳過的通知（跨重新載入記住；B4-01「重新載入就重跳舊警報」） ----
// localStorage：{ since: 看過的最新 created_at, ids: 最近 200 個跳過的 nid }。整頁重新載入、換頁、開新分頁都不重跳。

const SEEN_KEY = 'kinio.vms.inboxSeen.v1';
const SEEN_MAX = 200;

export interface InboxSeen {
  since: string;
  ids: string[];
}

export function loadInboxSeen(): InboxSeen {
  try {
    const v = JSON.parse(localStorage.getItem(SEEN_KEY) ?? '') as Partial<InboxSeen>;
    return { since: typeof v.since === 'string' ? v.since : '', ids: Array.isArray(v.ids) ? v.ids.filter((x): x is string => typeof x === 'string').slice(-SEEN_MAX) : [] };
  } catch {
    return { since: '', ids: [] };
  }
}

export function saveInboxSeen(since: string, ids: Iterable<string>): void {
  try {
    localStorage.setItem(SEEN_KEY, JSON.stringify({ since, ids: [...ids].slice(-SEEN_MAX) }));
  } catch {
    /* 私密模式：只在這一頁記 */
  }
}

/** 起點：這次第一次讀到的最新一筆、上次記下的、這一頁載入的時間（floor），取最新的（舊通知一律不跳）。 */
export function toastSince(firstNewest: string | undefined, saved: string, floor = ''): string {
  const ms = Math.max(0, ...[firstNewest ?? '', saved, floor].map((x) => Date.parse(x) || 0));
  return [firstNewest ?? '', saved, floor].find((x) => x && Date.parse(x) === ms) ?? new Date(0).toISOString();
}

/** 測試用 */
export function resetPopupBus(): void {
  popped.clear();
  active = 0;
  inboxSubs.clear();
}

/** 浮窗多久沒有操作就收成細條（SPEC UX-14 ⑥）。 */
export const POPUP_COLLAPSE_MS = 20_000;

/** 休眠文案只給電池 / HaLow 設備（B3-03）：HaLow 接的子設備、或有低功耗能力（kinio_power / kinio_halow）的設備；
 *  NVR 有線通道、插電攝影機寫「等第一個畫面」。 */
export function mayBeAsleep(dev: { capabilities?: string[]; parentAid?: string | null; protocol?: string }): boolean {
  if (dev.parentAid) return dev.protocol === 'halow';
  const caps = dev.capabilities ?? [];
  return caps.includes('kinio_power') || caps.includes('kinio_halow');
}
